<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Plus, Sparkles } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import AppAICreditsTokenDialog from '@/components/molecules/ai/AppAICreditsTokenDialog.vue'
import { useAiCreditsStore } from '@/stores/useAiCreditsStore'
import { useAuthStore } from '@/stores/useAuthStore'

interface Props {
  collapsed?: boolean
  compact?: boolean
  allowPurchase?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
  compact: false,
  allowPurchase: false,
})

const authStore = useAuthStore()
const aiCreditsStore = useAiCreditsStore()
const { locale } = useI18n()
const { isAuthenticated } = storeToRefs(authStore)
const { balanceCredits, isLoading, hasError } = storeToRefs(aiCreditsStore)
const showTokenDialog = ref(false)

const canRender = computed(() => isAuthenticated.value)
const tooltipText = 'Créditos Nebula'

const totalTokensTooltip = computed(() => {
  if (hasError.value || balanceCredits.value === null || Number.isNaN(balanceCredits.value)) {
    return tooltipText
  }

  return new Intl.NumberFormat(locale.value === 'en' ? 'en-US' : 'es-CO', {
    minimumFractionDigits: balanceCredits.value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(balanceCredits.value)
})

const formattedTokens = computed(() => {
  if (hasError.value) return '--'
  if (balanceCredits.value === null || Number.isNaN(balanceCredits.value)) {
    return isLoading.value ? '...' : '--'
  }

  const absoluteTokens = Math.abs(balanceCredits.value)
  if (absoluteTokens >= 1_000_000) return `${Math.trunc(balanceCredits.value / 1_000_000)}M`
  if (absoluteTokens >= 1_000) return `${Math.trunc(balanceCredits.value / 1_000)}K`

  return new Intl.NumberFormat(locale.value === 'en' ? 'en-US' : 'es-CO', {
    minimumFractionDigits: balanceCredits.value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(balanceCredits.value)
})

const refreshCredits = () => {
  void aiCreditsStore.refreshCredits({ force: true }).catch(() => {})
}

const openPurchaseDialog = () => {
  showTokenDialog.value = true
}

watch(
  isAuthenticated,
  (authenticated) => {
    if (!authenticated) {
      aiCreditsStore.clearCredits()
      return
    }

    void aiCreditsStore.refreshCredits().catch(() => {})
  },
  { immediate: true },
)
</script>

<template>
  <div
    v-if="canRender"
    class="group transition-all duration-200"
    :class="
      collapsed
        ? 'absolute -right-2 -top-2 z-10'
        : props.compact
          ? 'flex shrink-0 items-center justify-center'
          : 'ml-auto flex shrink-0 items-center justify-center'
    "
  >
    <div
      v-if="collapsed"
      class="flex min-w-9 items-center justify-center overflow-hidden rounded-lg border border-(--p-primary-color)/20 bg-white text-[10px] font-semibold leading-none text-(--p-primary-color) shadow-sm transition-all duration-200 group-hover:-translate-y-0.5 dark:border-(--p-primary-color)/30 dark:bg-(--p-surface-900)"
    >
      <button
        type="button"
        class="flex items-center gap-0.5 px-1.5 py-1"
        aria-label="Refrescar créditos"
        @click.stop="refreshCredits"
      >
        <Sparkles
          v-tooltip.top="tooltipText"
          class="h-3 w-3"
          :class="{ 'animate-pulse': isLoading }"
        />
        <span v-tooltip.top="totalTokensTooltip">{{ formattedTokens }}</span>
      </button>
      <button
        v-if="allowPurchase"
        type="button"
        class="flex h-full items-center border-l border-(--p-primary-color)/15 px-1 py-1"
        aria-label="Comprar tokens"
        @click.stop="openPurchaseDialog"
      >
        <Plus class="h-3 w-3" />
      </button>
    </div>

    <div
      v-else
      class="flex items-center overflow-hidden rounded-xl border border-(--p-primary-color)/20 bg-(--p-primary-color)/10 text-(--p-primary-color) transition-all duration-200 hover:bg-(--p-primary-color)/15 dark:border-(--p-primary-color)/20 dark:bg-(--p-primary-color)/10"
    >
      <button
        type="button"
        class="flex items-center gap-1.5 px-2.5 py-1.5"
        aria-label="Refrescar créditos"
        @click.stop="refreshCredits"
      >
        <Sparkles
          v-tooltip.top="tooltipText"
          class="h-4 w-4 shrink-0"
          :class="{ 'animate-pulse': isLoading }"
        />
        <!-- <span v-tooltip.top="totalTokensTooltip" class="text-xs font-semibold leading-none">
          {{ formattedTokens }}
        </span> -->
      </button>
      <button
        v-if="allowPurchase"
        type="button"
        class="flex h-full items-center justify-center border-l border-(--p-primary-color)/15 px-2.5 py-1.5 transition-colors hover:bg-(--p-primary-color)/10"
        aria-label="Comprar tokens"
        @click.stop="openPurchaseDialog"
      >
        <Plus class="h-4 w-4" />
      </button>
    </div>

    <AppAICreditsTokenDialog v-model:modelValue="showTokenDialog" />
  </div>
</template>
